// The learn path's trail, laid out once for web and native to draw from.

import type { GameState } from "@aybuben/core/state.ts";

import type { UiKey } from "../i18n/en.ts";
import { rng } from "../world/kit/shapes.ts";
import { fmt } from "../world/path.ts";
import type { SceneryPart } from "../world/trail-scenery.ts";
import { lessonNode, type HomeUnit, type LessonNodeState } from "./home.ts";

export const NODE_R = 24;
export const ROW = 88;
export const STUB = 28;
export const LABEL_GAP = 12;
export const LABEL_W = 120;
export const MIN_WIDTH = 288;
export const SCENERY_MIN = 56;
export const SCENERY_MAX = 72;

const WAVE = [0.15, 0.6, 1, 0.75, 0.3, 0];
const PARTS: readonly SceneryPart[] = ["apricot-tree", "poplar", "khachkar"];

export interface TrailNodePos {
	lessonId: string;
	x: number;
	y: number;
	current: boolean;
}
export interface TrailSegment {
	d: string;
	walked: boolean;
}
export interface ScenerySpot {
	part: SceneryPart;
	x: number;
	y: number;
	size: number;
}
export interface TrailLayout {
	width: number;
	height: number;
	nodes: TrailNodePos[];
	segments: TrailSegment[];
	labelX: (node: TrailNodePos) => number;
	scenery: ScenerySpot[];
}

const labelX = (n: TrailNodePos) => n.x + NODE_R + LABEL_GAP;

/**
 * FNV-1a: a small hash whose integer maths (Math.imul) gives the same result in
 * every JS engine, so web and native pick the same scenery for a unit.
 */
function fnv1a(s: string): number {
	let h = 2166136261;
	for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
	return h >>> 0;
}

function curve(ax: number, ay: number, bx: number, by: number): string {
	const dy = (by - ay) / 3;
	return `M${fmt(ax)} ${fmt(ay)} C${fmt(ax)} ${fmt(ay + dy)} ${fmt(bx)} ${fmt(by - dy)} ${fmt(bx)} ${fmt(by)}`;
}

/** `lessons[i].done`: lesson i counts as walked past (its node state is "done"). */
export function trailLayout(args: {
	unitId: string;
	lessons: readonly { lessonId: string; current: boolean; done: boolean }[];
	width: number;
}): TrailLayout {
	const { unitId, lessons } = args;
	const width = Number.isFinite(args.width) && args.width > MIN_WIDTH ? args.width : MIN_WIDTH;
	const n = lessons.length;
	if (n === 0) return { width, height: 0, nodes: [], segments: [], labelX, scenery: [] };

	const amp = Math.min(110, Math.max(56, width * 0.22));
	const nodes: TrailNodePos[] = lessons.map((l, i) => ({
		lessonId: l.lessonId,
		x: NODE_R + 6 + amp * WAVE[i % WAVE.length]!, // modulo keeps the index in range
		y: STUB + NODE_R + i * ROW,
		current: l.current,
	}));
	const first = nodes[0]!; // n > 0
	const last = nodes[n - 1]!;
	const height = last.y + NODE_R + STUB;

	const firstOpen = lessons.findIndex((l) => !l.done);
	const c = firstOpen === -1 ? n : firstOpen;
	const started = c > 0 || lessons.some((l) => l.current);
	const segments: TrailSegment[] = [];
	for (let k = 0; k <= n; k++) {
		const d =
			k === 0
				? curve(first.x, 0, first.x, first.y)
				: k === n
					? curve(last.x, last.y, last.x, height)
					: curve(nodes[k - 1]!.x, nodes[k - 1]!.y, nodes[k]!.x, nodes[k]!.y); // 0 < k < n
		segments.push({ d, walked: started && k <= c });
	}

	const zoneX = NODE_R + 6 + amp + NODE_R + LABEL_GAP + LABEL_W;
	const zoneW = width - 8 - zoneX;
	const scenery: ScenerySpot[] = [];
	if (zoneW >= SCENERY_MIN) {
		// seeded by the unit, so the scenery never moves when progress does
		const seed = fnv1a(unitId);
		const r = rng(seed);
		const size = Math.min(zoneW, SCENERY_MAX);
		const rows = Array.from({ length: n }, (_, i) => i);
		// Shuffled, so a unit never repeats a part.
		const parts = [...PARTS];
		for (let i = parts.length - 1; i > 0; i--) {
			const j = Math.floor(r() * (i + 1));
			[parts[i], parts[j]] = [parts[j]!, parts[i]!]; // i and j are within parts
		}
		for (let i = Math.min(seed % 3, Math.floor(n / 2)); i > 0; i--) {
			const part = parts[i - 1]!; // i <= 2 < parts.length
			const row = rows.splice(Math.floor(r() * rows.length), 1)[0]!; // rows holds at least n - 1 entries
			// row is an index drawn from rows, which holds only 0..n-1
			scenery.push({ part, x: zoneX + (zoneW - size) * r(), y: nodes[row]!.y - size / 2, size });
		}
	}
	return { width, height, nodes, segments, labelX, scenery };
}

/** Indices of segments walked in `now` but not in `before` (same unit); none without a `before`. */
export function newlyWalked(before: TrailLayout | undefined, now: TrailLayout): number[] {
	if (!before) return [];
	return now.segments.flatMap((s, k) => (s.walked && !before.segments[k]?.walked ? [k] : []));
}

/** A unit's trail for the learner's progress; also where the current node's y comes from. */
export function unitTrail(hu: HomeUnit, st: GameState, width: number): TrailLayout {
	const { unit: u, lessons } = hu;
	return trailLayout({
		unitId: u.id,
		lessons: lessons.map((l) => ({
			lessonId: l.lesson.id,
			current: l.isCurrent,
			done: lessonNode(st, u, l.lesson, l.offline).times > 0,
		})),
		width,
	});
}

/**
 * The line under a node's name. `current` is the ringed, pom-coloured one: only
 * an open lesson can be it, so an offline or locked current node reads as plain.
 */
export function nodeLine(
	state: LessonNodeState,
	isCurrent: boolean,
	started: boolean,
): { key: UiKey; current: boolean } {
	const current = isCurrent && state === "open";
	const key: UiKey =
		state === "locked"
			? "home.btnLocked"
			: state === "offline"
				? "home.btnOffline"
				: state === "done"
					? "home.lessonDone"
					: current
						? started
							? "home.keepGoing"
							: "home.startHere"
						: "home.btnStartLesson";
	return { key, current };
}
